// Navneskiltene på kortet (Spor: kortet). Ren geometri, så placeringen kan testes uden Pixi.
//
// Reglerne:
//  - Et navn står ved sin egen node: på en af otte faste pladser, højst 4 px fra husets kant.
//  - To navne dækker aldrig hinanden, og et navn dækker aldrig en anden nodes hus. Er der ikke plads,
//    brydes navnet i to linjer eller forkortes ("Datacenter Hinnerup" bliver "DC Hinnerup"), før det
//    lægger sig oven i noget.
//  - Byer, datacentre og værker har altid et navn. Byer i nød placeres først.
//  - Et navn bliver på sin plads, så længe den er fri: ingen hop fra billede til billede.

export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface Size {
  w: number;
  h: number;
}

export interface LabelNode {
  id: string;
  /** Nodens fodpunkt på skærmen. */
  x: number;
  y: number;
  /** Husets halve bredde, top og bund på skærmen. */
  half: number;
  top: number;
  bottom: number;
  /** Højere først (valgt, nævnt af træneren, i nød, by, værk, mølle). */
  prio: number;
  /** Byer, datacentre og værker: navnet vises altid. */
  must: boolean;
  /**
   * Navnets størrelse i hver variant: 0 fuldt navn, 1 brudt i to linjer, 2 forkortet, 3 forkortet med
   * lille skrift (sidste udvej, se SMALL). Null: findes ikke.
   */
  sizes: readonly (Size | null)[];
}

export interface Placed {
  slot: number;
  variant: number;
  /** Navnets midte (vandret) og top. */
  x: number;
  y: number;
  box: Box;
}

export interface LabelMemory {
  /** Hvor navnet stod sidst. */
  last: Map<string, { slot: number; variant: number }>;
  /** Hvor mange billeder dets sidste plads har været dækket af en boble eller et hint. */
  blockedFor: Map<string, number>;
  /**
   * Spor: telefonkort: så mange billeder endnu står navnene fra den systematiske søgning, så længe de
   * stadig er gyldige, uden at alt regnes forfra (søgningen koster et par ms på en telefon).
   */
  held?: number;
}

export interface LabelOpts {
  /** DOM-flader og svævende tal, navne helst går uden om. */
  obstacles: readonly Box[];
  /** Feltet, navnene helst står inden for (skærmen minus værktøjslinjen på siden). */
  band: Box;
  /** Spor: telefonkort: nodernes faste små ting (skilte, hjerter). Et navn går uden om dem som om et hus. */
  objects?: readonly Box[];
  /**
   * Spor: telefonkort: de flader blandt `obstacles`, der står længe (hintet). En boble dækker et
   * øjeblik, og navnet bliver stående; under hintet flytter navnet i samme billede (Hobro på bane 5
   * stod 0,4 s under et hint på tre linjer, hvor pladsen til højre for huset var fri).
   */
  firm?: readonly Box[];
}

/** Afstanden fra husets kant til navnet (px). Kriteriet er højst 12 px; testen tillader 16. */
export const LABEL_GAP = 3;
/** Så mange billeder må en boble dække et navns plads, før navnet flytter (ca. 0,4 s). */
const KEEP_FRAMES = 24;
export const SLOTS = 8;
/**
 * Spor: telefonkort: fra denne variant er skriften lille (12 px). Den prøves først, når ingen andre
 * former kan stå nogen steder: Brønderslev på bane 5 (telefon på tværs) var lukket inde af Hjørring,
 * Jammerbugt, Blokhus, Aalborg og Reserveværket, og navnet har ingen kort form.
 */
export const SMALL = 3;

/**
 * Spor: telefonkort: forskudte pladser, når de otte faste er taget. Et navn under eller over huset må
 * glide op til 32 px til siden, et navn ved siden af 14 px op eller ned; det rører stadig sit hus.
 * Plads `s + SLOTS * k` er plads `s` forskudt `SHIFTS[s][k]`. Klyngen ved Aarhus på bane 4 (telefon på
 * højkant) havde ingen ren løsning med de otte faste pladser, men en med de forskudte.
 */
const SHIFTS: readonly (readonly number[])[] = [
  [0, -8, 8, -16, 16, -24, 24, -32, 32],
  [0, -8, 8, -16, 16, -24, 24, -32, 32],
  [0, -8, 8, -14, 14],
  [0, -8, 8, -14, 14],
  [0],
  [0],
  [0],
  [0],
];

export function emptyMemory(): LabelMemory {
  return { last: new Map(), blockedFor: new Map() };
}

/** Pladsen for et navn af størrelse `s` ved noden: 0 under, 1 over, 2 højre, 3 venstre, 4-7 skråt. */
export function slotRect(n: LabelNode, s: Size, at: number): { x: number; y: number; box: Box } {
  const g = LABEL_GAP;
  const slot = at % SLOTS;
  const shift = SHIFTS[slot]?.[Math.floor(at / SLOTS)] ?? 0;
  const midY = (n.top + n.bottom) / 2;
  let x = n.x;
  let y = n.bottom + g;
  switch (slot) {
    case 1:
      y = n.top - g - s.h;
      break;
    case 2:
      x = n.x + n.half + g + s.w / 2;
      y = midY - s.h / 2;
      break;
    case 3:
      x = n.x - n.half - g - s.w / 2;
      y = midY - s.h / 2;
      break;
    case 4:
      x = n.x + n.half * 0.5 + s.w / 2;
      break;
    case 5:
      x = n.x - n.half * 0.5 - s.w / 2;
      break;
    case 6:
      x = n.x + n.half * 0.5 + s.w / 2;
      y = n.top - g - s.h;
      break;
    case 7:
      x = n.x - n.half * 0.5 - s.w / 2;
      y = n.top - g - s.h;
      break;
    default:
      break;
  }
  if (slot === 2 || slot === 3) y += shift;
  else x += shift;
  return { x, y, box: { x0: x - s.w / 2, y0: y, x1: x + s.w / 2, y1: y + s.h } };
}

export function overlap(a: Box, b: Box, shrink = 0): boolean {
  return a.x0 + shrink < b.x1 && b.x0 + shrink < a.x1 && a.y0 + shrink < b.y1 && b.y0 + shrink < a.y1;
}

/** Afstanden mellem to rektangler (0, når de rører hinanden). */
export function gap(a: Box, b: Box): number {
  const dx = Math.max(0, a.x0 - b.x1, b.x0 - a.x1);
  const dy = Math.max(0, a.y0 - b.y1, b.y0 - a.y1);
  return Math.hypot(dx, dy);
}

/** Hvor meget to rektangler dækker hinanden (areal). */
export function coverArea(a: Box, b: Box): number {
  return Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0)) * Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));
}

/** Husets krop på skærmen. */
export function bodyBox(n: LabelNode): Box {
  return { x0: n.x - n.half, y0: n.top, x1: n.x + n.half, y1: n.bottom };
}

/**
 * Grådig placering i prioritetsorden. Hver node prøver først sin sidste plads og variant, så alle
 * otte pladser med det fulde navn, så det brudte og til sidst det forkortede. Et navn, der ikke kan
 * stå frit, vises kun, hvis det skal (by, datacenter, værk, valgt eller nævnt), og så på den plads,
 * der dækker mindst. Spor: telefonkort: fik nogen ikke en ren plads, prøves en runde til, hvor de
 * vælger først; den bedste af de to vinder.
 */
export function layoutLabels(nodes: readonly LabelNode[], opts: LabelOpts, mem: LabelMemory, show: (n: LabelNode) => boolean = (n) => n.must): Map<string, Placed> {
  const prio = [...nodes].sort((a, b) => b.prio - a.prio || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const bodies = nodes.map((n) => ({ id: n.id, box: bodyBox(n) }));
  const byId = new Map(nodes.map((n) => [n.id, n]));
  // Kroppens midte: et navn skal stå nærmere sin egen node end nogen anden (IGN-løkke runde 4).
  const mids = nodes.map((n) => {
    const h = n.bottom - n.top;
    return { id: n.id, box: { x0: n.x - n.half * 0.6, y0: n.top + h * 0.2, x1: n.x + n.half * 0.6, y1: n.bottom } };
  });
  const { band, obstacles } = opts;
  const objects = opts.objects ?? [];
  const hitsObstacle = (r: Box) => obstacles.some((o) => overlap(o, r, 1));
  const hitsFirm = (r: Box) => (opts.firm ?? []).some((o) => overlap(o, r, 1));
  const hitsObject = (r: Box) => objects.some((o) => coverArea(o, r) > 4);
  const inBand = (r: Box) => r.x0 >= band.x0 && r.x1 <= band.x1 && r.y0 >= band.y0 && r.y1 <= band.y1;

  /** Prøvemålene for node `n`, når navnene i `taken` allerede står. */
  const judge = (n: LabelNode, taken: readonly Box[]) => {
    const hitsLabel = (r: Box) => taken.some((o) => overlap(o, r));
    const hitsBody = (r: Box) => bodies.some((b) => b.id !== n.id && overlap(b.box, r, 2));
    const own = (r: Box) => {
      let mine = Infinity;
      let other = Infinity;
      for (const m of mids) {
        const d = gap(m.box, r);
        if (m.id === n.id) mine = d;
        else other = Math.min(other, d);
      }
      return mine <= other - 2;
    };
    const clean = (r: Box) => !hitsLabel(r) && !hitsBody(r) && !hitsObject(r);
    // Husets kerne (det, øjet læser som huset): et navn må strejfe en nabos tagskæg eller fod, hvis
    // det ellers skulle ud over skærmkanten (Odense på bane 6, Studstrupværket på bane 4).
    const hitsCore = (r: Box) =>
      bodies.some((b) => {
        if (b.id === n.id) return false;
        const w = b.box.x1 - b.box.x0;
        const h = b.box.y1 - b.box.y0;
        return coverArea({ x0: b.box.x0 + w * 0.18, y0: b.box.y0 + h * 0.18, x1: b.box.x1 - w * 0.18, y1: b.box.y1 - h * 0.18 }, r) > 16;
      });
    const grazes = (r: Box) => !hitsLabel(r) && !hitsCore(r) && !hitsObject(r);
    const free = (r: Box) => clean(r) && !hitsObstacle(r) && inBand(r) && own(r);
    const at = (slot: number, variant: number) => {
      const s = n.sizes[variant];
      if (!s) return null;
      const r = slotRect(n, s, slot);
      // Spor: telefonkort: et navn over eller under huset glider ind fra skærmkanten i stedet for at
      // blive klippet ("iborg"). Det står stadig over eller under sit eget hus.
      if (slot % SLOTS === 2 || slot % SLOTS === 3 || band.x1 - band.x0 < s.w) return r;
      const dx = r.box.x0 < band.x0 ? band.x0 - r.box.x0 : r.box.x1 > band.x1 ? band.x1 - r.box.x1 : 0;
      if (dx === 0 || Math.abs(dx) > s.w / 2) return r;
      return { x: r.x + dx, y: r.y, box: { x0: r.box.x0 + dx, y0: r.box.y0, x1: r.box.x1 + dx, y1: r.box.y1 } };
    };
    const tries: Array<[number, number]> = [];
    // De forskudte pladser kommer efter alle faste: et navn står helst midt for sit hus. Den lille
    // skrift (variant SMALL og frem) prøves først, når ingen af de andre former kan stå nogen steder.
    for (const [v0, v1] of [
      [0, SMALL],
      [SMALL, n.sizes.length],
    ] as const) {
      for (let v = v0; v < v1; v++) {
        if (!n.sizes[v]) continue;
        for (let s = 0; s < SLOTS; s++) tries.push([s, v]);
      }
      for (let v = v0; v < v1; v++) {
        if (!n.sizes[v]) continue;
        for (let s = 0; s < SLOTS; s++) for (let k = 1; k < (SHIFTS[s]?.length ?? 1); k++) tries.push([s + SLOTS * k, v]);
      }
    }
    // Oven i et andet navn eller hus vejer tungest, uden for feltet (klippet af kanten) næst, og et
    // navn, der kun strejfer en nabos hus, mindst.
    const miss = (r: Box) => (clean(r) ? 0 : grazes(r) ? 1 : 3) + (inBand(r) ? 0 : 2);
    return { hitsBody, own, clean, grazes, free, at, tries, miss };
  };

  /** Én grådig runde i rækkefølgen `order`. `worst` vejer navne, der ikke står rent eller i feltet. */
  const pass = (order: readonly LabelNode[], mem: LabelMemory): { out: Map<string, Placed>; bad: string[]; worst: number } => {
    let out = new Map<string, Placed>();
    const taken: Box[] = [];
    let bad: string[] = [];
    let worst = 0;
    const misses = new Map<string, number>();
    for (const n of order) {
      const { own, clean, grazes, free, at, tries, miss: missOf } = judge(n, taken);
      const last = mem.last.get(n.id);
      let pick: [number, number] | undefined;
      if (last && n.sizes[last.variant]) {
        const lr = at(last.slot, last.variant);
        if (lr && free(lr.box)) {
          pick = [last.slot, last.variant];
          mem.blockedFor.delete(n.id);
          // Blev det forkortede navn valgt i trængsel, får det sit fulde navn igen på samme plads,
          // når der er blevet plads (samme plads: navnet flytter sig ikke).
          for (let v = 0; v < last.variant; v++) {
            const r = at(last.slot, v);
            if (r && free(r.box)) {
              pick = [last.slot, v];
              break;
            }
          }
        } else if (lr && clean(lr.box) && own(lr.box) && inBand(lr.box) && !hitsFirm(lr.box) && (mem.blockedFor.get(n.id) ?? 0) < KEEP_FRAMES) {
          // Kun en boble dækker et øjeblik: navnet bliver stående (Varde hoppede). Hintet står længe.
          pick = [last.slot, last.variant];
          mem.blockedFor.set(n.id, (mem.blockedFor.get(n.id) ?? 0) + 1);
        } else mem.blockedFor.delete(n.id);
      }
      pick ??= tries.find(([s, v]) => free((at(s, v) as { box: Box }).box));
      if (!pick && show(n)) {
        // Ingen helt fri plads: først en, der kun ligger under en boble, men aldrig oven i et andet navn
        // eller hus; så en i feltet, der kun strejfer en nabos hus uden for kernen; så en i feltet, der
        // står lidt nærmere en nabo; så en uden for feltet; så en, der ikke dækker et navn; til sidst
        // den mindste dækning.
        // Feltet kommer før boblen, for uden for feltet kan være uden for skærmen (break-ui ved 320 px:
        // "Ringkøbing Havvindmø" stod halvt ude, hvor det korte "Ringkøbing" kunne stå under hintet).
        const box = (t: [number, number]) => (at(t[0], t[1]) as { box: Box }).box;
        // Spor: telefonkort: står den sidste plads i samme trin som den første, der findes, bliver
        // navnet (ingen hop mellem to lige gode pladser, Datacenter Ulstrup på bane 4).
        const lastT: [number, number] | undefined = last && n.sizes[last.variant] ? [last.slot, last.variant] : undefined;
        const tier = (ok: (r: Box) => boolean) => (lastT && ok(box(lastT)) ? lastT : tries.find((t) => ok(box(t))));
        // Spor: telefonkort: men et navn, der strejfer en nabos tagskæg eller står lidt nærmere en nabo,
        // kan læses; et navn under hintet kan ikke, og hintet står længe (Holstebro i bredformat på
        // bane 1 stod under det, hvor siden af huset lå en pixel nærmere Hernings hus).
        const open = (r: Box) => grazes(r) && inBand(r) && !hitsObstacle(r);
        pick =
          tier((r) => open(r) && own(r)) ??
          tier((r) => open(r) && clean(r)) ??
          tier(open) ??
          tier((r) => clean(r) && own(r) && inBand(r)) ??
          tier((r) => grazes(r) && own(r) && inBand(r)) ??
          // Spor: telefonkort: hellere i feltet lidt nærmere en nabos midte end ude over kanten
          // (Odense på bane 6 stod ved x -43; under huset var 1 px nærmere Solpark Ringe).
          tier((r) => clean(r) && inBand(r)) ??
          tier((r) => grazes(r) && inBand(r)) ??
          tier((r) => clean(r) && own(r)) ??
          tier((r) => clean(r));
        if (!pick) {
          const cost = ([s, v]: [number, number]) => {
            const b = (at(s, v) as { box: Box }).box;
            return (
              taken.reduce((sum, o) => sum + coverArea(o, b) * 3, 0) +
              bodies.reduce((sum, o) => sum + (o.id === n.id ? 0 : coverArea(o.box, b)), 0) +
              objects.reduce((sum, o) => sum + coverArea(o, b), 0)
            );
          };
          pick = tries.reduce((best, t) => (cost(t) < cost(best) ? t : best), tries[tries.length - 1] as [number, number]);
        }
      }
      if (!pick) {
        mem.last.delete(n.id);
        continue;
      }
      const r = at(pick[0], pick[1]) as { x: number; y: number; box: Box };
      const miss = missOf(r.box);
      if (miss) {
        bad.push(n.id);
        worst += miss;
        misses.set(n.id, miss);
      }
      out.set(n.id, { slot: pick[0], variant: pick[1], x: r.x, y: r.y, box: r.box });
      taken.push(r.box);
      mem.last.set(n.id, { slot: pick[0], variant: pick[1] });
    }
    // Spor: telefonkort: et navn uden ren plads må skubbe et eller to naboers navne, hvis de selv kan stå
    // helt frit et andet sted (Studstrupværket ude over kanten på bane 4, fordi DC Hinnerups fulde
    // navn stod på den eneste plads over værket, mens "DC Hinnerup" kunne stå under sit eget hus).
    for (const id of [...bad]) {
      const n = byId.get(id);
      if (!n) continue;
      const rest = [...out].filter(([k]) => k !== id);
      const me = judge(n, []);
      let done = false;
      for (const [s, v] of me.tries) {
        const r = me.at(s, v);
        if (!r || !inBand(r.box) || me.hitsBody(r.box) || hitsObject(r.box) || hitsObstacle(r.box) || !me.own(r.box)) continue;
        const pushed = rest.filter(([k, p]) => !misses.has(k) && overlap(p.box, r.box)).map(([k]) => k);
        if (!pushed.length || pushed.length > 2 || rest.some(([k, p]) => misses.has(k) && overlap(p.box, r.box))) continue;
        const trial = new Map(out);
        trial.set(id, { slot: s, variant: v, x: r.x, y: r.y, box: r.box });
        for (const k of pushed) trial.delete(k);
        let ok = true;
        for (const k of pushed) {
          const m = byId.get(k) as LabelNode;
          const j = judge(m, [...trial.values()].map((p) => p.box));
          const t = j.tries.find(([s2, v2]) => {
            const r2 = j.at(s2, v2);
            return r2 !== null && j.free(r2.box);
          });
          if (!t) {
            ok = false;
            break;
          }
          const r2 = j.at(t[0], t[1]) as { x: number; y: number; box: Box };
          trial.set(k, { slot: t[0], variant: t[1], x: r2.x, y: r2.y, box: r2.box });
        }
        if (!ok) continue;
        out = trial;
        for (const k of [id, ...pushed]) {
          const p = trial.get(k) as Placed;
          mem.last.set(k, { slot: p.slot, variant: p.variant });
        }
        worst -= misses.get(id) ?? 0;
        misses.delete(id);
        done = true;
        break;
      }
      if (done) bad = bad.filter((k) => k !== id);
    }
    return { out, bad, worst };
  };

  const copy = (m: LabelMemory): LabelMemory => ({ last: new Map(m.last), blockedFor: new Map(m.blockedFor) });
  const keep = (m: LabelMemory, out: Map<string, Placed>): Map<string, Placed> => {
    mem.last = m.last;
    mem.blockedFor = m.blockedFor;
    mem.held = m.held ?? 0;
    return out;
  };
  // Står navnene fra søgningen stadig i feltet uden at dække noget, bliver de (hurtigt og uden hop).
  if (mem.held && mem.held > 0) {
    const out = new Map<string, Placed>();
    const taken: Box[] = [];
    let ok = true;
    for (const n of prio) {
      const last = mem.last.get(n.id);
      if (!last || !n.sizes[last.variant]) {
        if (show(n)) ok = false;
        if (!ok) break;
        continue;
      }
      const j = judge(n, taken);
      const r = j.at(last.slot, last.variant);
      if (!r || !inBand(r.box) || !j.grazes(r.box) || hitsObstacle(r.box)) {
        ok = false;
        break;
      }
      out.set(n.id, { slot: last.slot, variant: last.variant, x: r.x, y: r.y, box: r.box });
      taken.push(r.box);
    }
    if (ok) {
      mem.held--;
      return out;
    }
    mem.held = 0;
  }
  const m1 = copy(mem);
  const first = pass(prio, m1);
  if (!first.bad.length) return keep(m1, first.out);
  // De navne, der ikke fik en ren plads, vælger først i en runde til ("St" ved Studstrupværket på
  // bane 4: DC Hammels navn havde taget pladsen under værket, men kunne selv stå et andet sted).
  // Næste billede prøver alle deres sidste plads først, så det bedre billede holder og ikke vipper.
  const lost = new Set(first.bad);
  const m2 = copy(mem);
  const lostFirst = [...prio.filter((n) => lost.has(n.id)), ...prio.filter((n) => !lost.has(n.id))];
  const second = pass(lostFirst, m2);
  let best = second.worst < first.worst ? { m: m2, r: second } : { m: m1, r: first };
  if (!best.r.bad.length) return keep(best.m, best.r.out);
  // Spor: telefonkort: navnene, der står, hvor de stod sidst, kan låse en klynge, så intet navn har en
  // ren plads (Aarhus-klyngen på bane 4). Så lægges navnene forfra uden hukommelse, og den nye
  // placering vinder kun, hvis den er bedre. Næste billede holder den, for så er alle pladser frie.
  let order: readonly LabelNode[] = prio;
  for (let round = 0; round < 3 && best.r.bad.length; round++) {
    const m = { last: new Map<string, { slot: number; variant: number }>(), blockedFor: new Map<string, number>() };
    const r = pass(order, m);
    if (r.worst < best.r.worst) best = { m, r };
    // Næste runde: de navne, der heller ikke her fik en ren plads, vælger først.
    const lostNow = new Set(r.bad);
    order = [...prio.filter((n) => lostNow.has(n.id)), ...prio.filter((n) => !lostNow.has(n.id))];
  }
  if (!best.r.bad.length) return keep(best.m, best.r.out);
  const found = search(best.r.out, best.m);
  // Søgningens navne står alle i feltet og højst på et tagskæg; den grådige har et navn på en
  // huskerne, et andet navn eller uden for feltet, når den vejer mere end ét pr. navn.
  const hard = best.r.worst > best.r.bad.length;
  if (found && (hard || found.worst < best.r.worst)) return keep(found.m, found.out);
  // Også den grådige holdes, hvis den er gyldig: så regnes klyngen ikke forfra i hvert billede.
  best.m.held = 60;
  return keep(best.m, best.r.out);

  /**
   * Spor: telefonkort: den grådige placering fandt ingen, hvor alle navne står i feltet uden at dække
   * et navn, en ting eller en nabos huskerne. Så søges der systematisk (med et loft), og et navn, der
   * kun strejfer en nabos tagskæg, er godt nok. Hvert navn prøver sin sidste plads først, så samme
   * billede giver samme svar, og en løsning fra sidste billede findes straks igen.
   */
  function search(cur: Map<string, Placed>, base: LabelMemory): { out: Map<string, Placed>; m: LabelMemory; worst: number } | null {
    type Cand = { slot: number; variant: number; x: number; y: number; box: Box; score: number } | null;
    const items: Array<{ n: LabelNode; cands: Cand[] }> = [];
    for (const n of prio) {
      if (!cur.has(n.id)) continue;
      const j = judge(n, []);
      const last = base.last.get(n.id);
      const seen = new Set<string>();
      const list: Array<{ slot: number; variant: number; x: number; y: number; box: Box; score: number; rank: number }> = [];
      for (const [sl, v] of j.tries) {
        const r = j.at(sl, v);
        if (!r || !inBand(r.box) || !j.grazes(r.box)) continue;
        const key = `${Math.round(r.box.x0)},${Math.round(r.box.y0)},${Math.round(r.box.x1)},${Math.round(r.box.y1)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        // Under en boble eller et hint kun, hvis intet andet går (bane 4: hintet over Bjerringbro lukkede
        // den eneste plads, hvor Datacenter Ulstrup kunne stå, og Studstrupværket røg ud over kanten).
        // Det tæller som et strejf: ellers slog en løsning med Holstebro under hintet den grådige, hvor
        // navnet kun rørte Solpark Hjerms tagskæg (bredformat, bane 1).
        const under = hitsObstacle(r.box);
        const score = (j.clean(r.box) ? 0 : 1) + (under ? 1 : 0);
        const isLast = last && last.slot === sl && last.variant === v;
        list.push({ slot: sl, variant: v, ...r, score, rank: (under ? 10 : 0) + (isLast ? -1 : score * 2 + (j.own(r.box) ? 0 : 1)) });
      }
      list.sort((a, b) => a.rank - b.rank);
      const cands: Cand[] = list.map(({ rank: _r, ...c }) => c);
      if (!show(n)) cands.push(null);
      if (!cands.length) return null;
      items.push({ n, cands });
    }
    items.sort((a, b) => a.cands.length - b.cands.length);
    const chosen: Cand[] = [];
    let steps = 0;
    const go = (i: number): boolean => {
      if (i === items.length) return true;
      for (const c of (items[i] as { cands: Cand[] }).cands) {
        if (++steps > 20000) return false;
        if (c && chosen.some((o) => o && overlap(o.box, c.box))) continue;
        chosen.push(c);
        if (go(i + 1)) return true;
        chosen.pop();
      }
      return false;
    };
    if (!go(0)) return null;
    const out = new Map<string, Placed>();
    const m: LabelMemory = { last: new Map(base.last), blockedFor: new Map(base.blockedFor), held: 60 };
    let worst = 0;
    items.forEach(({ n }, i) => {
      const c = chosen[i];
      if (!c) {
        m.last.delete(n.id);
        return;
      }
      out.set(n.id, { slot: c.slot, variant: c.variant, x: c.x, y: c.y, box: c.box });
      m.last.set(n.id, { slot: c.slot, variant: c.variant });
      m.blockedFor.delete(n.id);
      worst += c.score;
    });
    return { out, m, worst };
  }
}

/** Hale-ord, der kan undværes, når ikonet selv siger det (møllen, solcellen, værket). */
const TAILS = [/\s+Havvindmøllepark$/i, /\s+Havvind$/i, /\s+Vind$/i, /-?kraftvarme(værk)?$/i, /\s+Fjernvarme$/i, /værket$/i, /\s+Strand$/i];

/**
 * Den korte form af et stednavn, når der ikke er plads til det hele. "Datacenter Hinnerup" bliver
 * "DC Hinnerup", "Studstrupværket" bliver "Studstrup", "Solpark Egtved" bliver "Sol Egtved". Er det
 * stadig langt, står første del ("Ringkøbing-Skjern ..." bliver "Ringkøbing"), og til sidst klippes
 * det med et punktum. Højst 12 tegn.
 */
export function shortName(name: string, max = 12): string {
  let s = name.trim().replace(/^Datacenter\s+/i, 'DC ').replace(/^Solpark\s+/i, 'Sol ');
  for (const t of TAILS) {
    const cut = s.replace(t, '').trim();
    if (cut.length >= 2) s = cut;
  }
  if (s.length > max) {
    const prefix = /^(DC|Sol)\s+/.exec(s)?.[0] ?? '';
    const rest = s.slice(prefix.length);
    const first = rest.split(/[\s-]+/)[0] ?? rest;
    if (first.length >= 2 && first.length < rest.length) s = `${prefix}${first}`;
  }
  if (s.length > max) s = `${s.slice(0, max - 1)}.`;
  return s;
}

